import { PropertyReport } from "../features/reports/reportTypes";

import { formatNumberRemoveCommas } from ".";

export const generateLivingSpacePhrase = (
  report: PropertyReport | undefined,
) => {
  let interiorSpacePhrase = "";
  if (
    report?.propertyDetails?.buildingSquareFeet &&
    report?.blockDetails?.medianSumSqft
  ) {
    const buildingSqftNum = formatNumberRemoveCommas(
      report.propertyDetails.buildingSquareFeet,
    );
    const medianSqftNum = Number(report.blockDetails.medianSumSqft);
    let sqftDifference = buildingSqftNum - medianSqftNum;

    let sqftPercentDifference =
      Math.abs(sqftDifference / Number(report.blockDetails.medianSumSqft)) *
      100;
    if (sqftPercentDifference <= 2) {
      interiorSpacePhrase = `Similar size to other homes in the area`;
    } else if (sqftDifference > 0) {
      interiorSpacePhrase = `Larger than other homes in the area`;
    } else if (sqftDifference < 0) {
      interiorSpacePhrase = `Smaller than other homes in the area`;
    }
  } else if (
    !report?.propertyDetails?.buildingSquareFeet &&
    report?.blockDetails?.medianSumSqft
  ) {
    interiorSpacePhrase = `Typical nearby home is ${report.blockDetails.medianSumSqft} sq ft`;
  } else {
    interiorSpacePhrase = "Building size not disclosed";
  }

  return interiorSpacePhrase;
};

export const generateLayoutPhrase = (report: PropertyReport | undefined) => {
  let layoutPhrase = "";

  const bd = report?.propertyDetails?.bedrooms;
  const baths = report?.propertyDetails?.totalBaths;
  const medianBd = report?.blockDetails?.medianBedroomCount;
  const medianBath = report?.blockDetails?.medianBathCount;

  const hasPropertyCounts =
    bd !== undefined && bd !== null && baths !== undefined && baths !== null;
  const hasNeighborhoodCounts =
    medianBd !== undefined &&
    medianBd !== null &&
    medianBath !== undefined &&
    medianBath !== null;

  if (hasPropertyCounts && hasNeighborhoodCounts) {
    // if all the counts are available
    if (bd === medianBd && baths === medianBath) {
      layoutPhrase = "Matches the layout of nearby homes";
    } else if (bd > medianBd && baths === medianBath) {
      layoutPhrase = "More bedrooms, same baths as nearby homes";
    } else if (bd < medianBd && baths === medianBath) {
      layoutPhrase = "Fewer bedrooms, same baths as nearby homes";
    } else if (bd === medianBd && baths > medianBath) {
      layoutPhrase = "Same bedrooms, more baths than nearby homes";
    } else if (bd === medianBd && baths < medianBath) {
      layoutPhrase = "Same bedrooms, fewer baths than nearby homes";
    } else if (bd < medianBd && baths < medianBath) {
      layoutPhrase = "Fewer bedrooms and baths than nearby homes";
    } else if (bd > medianBd && baths > medianBath) {
      layoutPhrase = "More bedrooms and baths than nearby homes";
    } else if (bd > medianBd && baths < medianBath) {
      layoutPhrase = "More bedrooms but fewer baths than nearby homes";
    } else if (bd < medianBd && baths > medianBath) {
      layoutPhrase = "More baths but fewer bedrooms than nearby homes";
    }
  } else if (!hasPropertyCounts && hasNeighborhoodCounts) {
    layoutPhrase = `Typical home ${medianBd}bd / ${medianBath}ba`;
  } else {
    layoutPhrase = "No neighborhood bed/bath data available";
  }
  return layoutPhrase;
};

export const generateLotSizePhrase = (report: PropertyReport | undefined) => {
  let lotSizePhrase = "";
  const rawLandSqft =
    report?.propertyAssessments && report.propertyAssessments.length > 0
      ? report.propertyAssessments[0].landSquareFootage
      : undefined;

  const propertySqFt =
    rawLandSqft !== undefined && rawLandSqft !== null
      ? Number(formatNumberRemoveCommas(String(rawLandSqft)))
      : NaN;

  const blockSqFt =
    report?.blockDetails?.medianSumSqft !== undefined &&
    report?.blockDetails?.medianSumSqft !== null
      ? Number(report.blockDetails.medianSumSqft)
      : NaN;

  let lotDifference = Number(propertySqFt) - Number(blockSqFt);
  let lotPercentDifference = Math.abs(lotDifference / Number(blockSqFt)) * 100;
  if (propertySqFt && blockSqFt) {
    if (propertySqFt > blockSqFt) {
      lotSizePhrase = "Larger than other lots in the area";
    } else if (propertySqFt < blockSqFt) {
      lotSizePhrase = "Smaller than other lots in the area";
    } else if (lotPercentDifference <= 5) {
      lotSizePhrase = "Similar to other lots in the area";
    }
  } else if (!blockSqFt && propertySqFt) {
    lotSizePhrase = "No market information available";
  } else if (!propertySqFt && blockSqFt) {
    lotSizePhrase = `Typical lot ≈ ${blockSqFt} sq ft `;
  }

  return lotSizePhrase;
};

export const generateConstructionPhrase = (
  report: PropertyReport | undefined,
) => {
  let constructionPhrase = "";
  let assessments = report?.propertyAssessments;
  let yearBuilt =
    assessments &&
    assessments.length > 0 &&
    assessments[0].yearBuilt !== undefined
      ? Number(assessments[0].yearBuilt)
      : NaN;
  let p25 =
    report?.blockDetails?.percentile25YearBuilt !== undefined
      ? Number(report.blockDetails.percentile25YearBuilt)
      : NaN;
  let p75 =
    report?.blockDetails?.percentile75YearBuilt !== undefined
      ? Number(report.blockDetails.percentile75YearBuilt)
      : NaN;

  let hasYearBuilt = !isNaN(yearBuilt);
  let hasP25 = !isNaN(p25);
  let hasP75 = !isNaN(p75);

  if (hasYearBuilt && hasP25 && hasP75) {
    if (yearBuilt > p75) {
      constructionPhrase = "Newer than most other homes in the neighborhood";
    } else if (yearBuilt < p75) {
      constructionPhrase = "Older than most other homes in the neighborhood";
    } else {
      constructionPhrase = "Similar in age to most homes in the neighborhood";
    }
  } else if (!hasP25 && !hasP75) {
    constructionPhrase = "No market information available";
  } else if (!hasYearBuilt && hasP25 && hasP75) {
    constructionPhrase = `Most homes built between ${p25} - ${p75}`;
  } else if (!hasYearBuilt && (hasP25 || hasP75)) {
    const year = hasP25 ? p25 : p75;
    constructionPhrase = `Most homes built in ${year}`;
  } else {
    constructionPhrase = "Construction year information unavailable";
  }

  return constructionPhrase;
};
